/***********************************************************************************************************************
 *                                                                                                                     *
 * V.1.0
 * User: Shahar Albag
 * Date: 15/01/14
 *                                                                                                      *
 *                                                                                                                     *
 * ********************************************************************************************************************/
define(/** @lends PlusCompositeView */ function (require) {

    "use strict";

    /////////////////////////////////////////////////////////////////////////////
    //                              Dependencies                               //
    /////////////////////////////////////////////////////////////////////////////
    var _ = require("underscore");
    var Wreqr = require("backbone.wreqr");
    var Logger = require('logger');
    var Marionette = require("marionette");
    var template = require('ui/plus-composite-view/templates/plusCompositeView.tmpl');
    var DefaultPlusComponentItemView = require('ui/plus-composite-view/js/views/DefaultPlusComponentItemView');
    var DefaultPlusComponentCollection = require('ui/plus-composite-view/js/collections/DefaultPlusComponentCollection');

    /**
     *
     *  --------------------------------------------------------
     *  Plus Composite view
     *  --------------------------------------------------------
     *
     *  A plus composite view is an extension of the composite view allowing showing first few items only
     *
     *  Collection: Any - received from calling method
     *  itemView: Any - received from calling method
     *
     *  Options:
     *  --------------------------------------------------------
     *      {
     *          textModelAttr: 'value',         // define the text Model Attribute (to display in tooltip)
     *          visibleItemsCount: 3,           // define how many items to show
     *          itemView: CustomItemView,   // define the itemView on CompositeView
     *          permutation: PlusCompositeView.PERMUTATIONS.DEFAULT // define the permutation for the composite view
     *      }
     *
     *  Methods:
     *  --------------------------------------------------------
     *  + setCollection
     *
     *  Usage Example:
     *  --------------------------------------------------------
     *
        var myCollection = new Collection([
         {
             code: "1", // rendered by itemView (not as part of this component)
             tooltipTxt: "first", // show on tooltip is hidden
         },
         {
             code: "2",
             tooltipTxt: "second",
         },
         {
             code: "3",
             tooltipTxt: "third",
         }
         ]);

        var myPlusCompositeView= new PlusCompositeView({
               textModelAttr: 'tooltipTxt',         // define the text Model Attribute (to display in tooltip)
               visibleItemsCount: 2,           // define how many items to show
               itemView: myItemView,   // define the itemView on CompositeView - external
               collection: myCollection,
               permutation: PlusCompositeView.PERMUTATIONS.DEFAULT // define the permutation for the composite view
        });

     */

    var DEFAULT_VISIBLE_ITEMS_COUNT = 3;
    var DEFAULT_TEXT_MODEL_ATTR = 'value';
    var DEFAULT_ESCAPE_HTML = true;

    var PERMUTATIONS = {
        UPPER:   'upper'
    };

    var PlusCompositeView = Marionette.CompositeView.extend({

        itemViewContainer: 'ul',
        template: template,
        ui: {
            ul: '.plus-composite-view  ul',
            plusCount: '.plus-composite-view .plusCount'
        },

        /**
         * Backbone call this method to get the options for the item views
         * @param model
         * @param index
         * @returns options
         */
        itemViewOptions: function(model, index) {
            var options = {};

            options.vent = this.vent;
            options.escapeHtml = this.escapeHtml;

            return options;
        },

        /**
         * We have a template helpers in order to use the ability of mustache to have a dynamic key.
         * We want to display the channel and view (from the dictionary) and need its keys from the dictionary.
         * The keys are using the name of the type/channel, so we get those from the model.
         * For example: "LECampaign.engagementDetailsView.type.peeling.text" - We get the text "peeling" from the engagement model according to the saved type in the model.
         * @return {Object}
         */
        customTemplateHelpers: function (){
            return {
                plusCount: this.plusCount,
                plusTooltip: this.plusTooltip,
                hidePlusCount: function() {
                    return (this.plusCount <= 0);
                }
            };
        },

        initialize: function (options) {
            this.vent = new Wreqr.EventAggregator();
            this.container = options.container;
            // Validate the options (throws exception if invalid)
            this._validateOptions(options);

            this.permutation = _.has(options, "permutation") ? options.permutation : PERMUTATIONS.DEFAULT;

            // set visible items count
            this.visibleItemsCount = _.has(options, "visibleItemsCount") ? options.visibleItemsCount : DEFAULT_VISIBLE_ITEMS_COUNT;
            // set text Model Attribute (to display in tooltip)
            this.textModelAttr = _.has(options, "textModelAttr") ? options.textModelAttr : DEFAULT_TEXT_MODEL_ATTR;
            // set whether to render escaped or unescaped HTML in the template
            this.escapeHtml = _.has(options, "escapeHtml") ? options.escapeHtml : DEFAULT_ESCAPE_HTML;

            // keep original Collection
            this.originalCollection = options.collection;

            // bind collection external changes events
            this._bindEvents();

            // Setup the collections
            this._setCollection();
            // calculate Peripherals
            //this._calcPeripherals();

            // Call super method
            Marionette.CompositeView.prototype.initialize.call(this, options);
        },

        // -------------------------------------------------------------------------------------------------------------

        _validateOptions: function(options){
            var logger = Logger.getLogger('PlusCompositeViewWidget');

            if (!options || !_.isPlainObject(options)) {
                throw "PlusCompositeView >> initialize >> invalid argument: options is not a valid object.";
            }

            if (!options.collection) {
                logger.error("collection is mandatory");
                throw "PlusCompositeView >> initialize >> validateOptions >> collection is mandatory";
            }
        },

        // -------------------------------------------------------------------------------------------------------------

        _bindEvents: function() {
            // listen to changes on original collection and re-render view
            this.listenTo(this.originalCollection, "add", _.bind(this._setCollection, this));
            this.listenTo(this.originalCollection, "reset",  _.bind(this._setCollection, this));
            this.listenTo(this.originalCollection, "remove",  _.bind(this._setCollection, this));
        },

        _bindInternalCollectionEvents: function() {
            this.listenTo(this.collection, "add", _.bind(this.onCollectionChange, this));
            this.listenTo(this.collection, "reset",  _.bind(this.onCollectionChange, this));
            this.listenTo(this.collection, "remove",  _.bind(this.onCollectionChange, this));
        },

        // -------------------------------------------------------------------------------------------------------------

        onCollectionChange: function(){
            this._calcPeripherals();
            // refresh view on collection Change
            this.render();
        },

        onRender: function() {
            if (this.container) {
                this.container.append(this.$el);
            }
            this.ui.ul.addClass(this.permutation);
        },

        // -------------------------------------------------------------------------------------------------------------

        _setCollection: function() {
            // Setup the collections
            this.collection = this.originalCollection.clone(); // The internal collection, used internally without affecting the external collection

            // bind the internal collection events - after the reference was changed
            this._bindInternalCollectionEvents();

            // take first elements (show only the required ones)
            this.collection.reset(this.collection.first(this.visibleItemsCount));
        },
        /**
         * this function calculate the plus count and the plus tooltip text.
         * @private
         */
        _calcPeripherals: function() {
            var hiddenCollection = this.originalCollection.last(_.size(this.originalCollection) - this.visibleItemsCount);
            this.plusCount = _.size(hiddenCollection);
            this.plusTooltip = this._buildPlusTooltip(hiddenCollection, this.textModelAttr);
        },

        _buildPlusTooltip: function (collection, modelAttr) {
            var txt = "";
            _.each(collection, function (model, index){
                if (index !== 0) {
                    txt += ", ";
                }
                txt += model.get(modelAttr);
            });
            return txt;
        }
    }); // EO PlusCompositeView

    // Externalize Permutations
    PlusCompositeView.PERMUTATIONS = PERMUTATIONS;
    PlusCompositeView.DefaultPlusComponentItemView   = DefaultPlusComponentItemView;
    PlusCompositeView.DefaultPlusComponentCollection = DefaultPlusComponentCollection;

    return PlusCompositeView;
});


